import React, { useState } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { MdDateRange } from "react-icons/md";

import { Button, Input, Select } from "../../../../../../components/Form";
import { Color } from "../../../../../../components/Form/Button";
import InputDateRange from "../../../../../../components/Form/Input/DateRange";

const Wrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  width: "fit-content",
  gap: 5,
  "& > div": {
    width: "fit-content",
  },
}));

const SelectStyled = styled(Select)(() => ({
  width: "fit-content",
  "& .MuiSelect-root": {
    marginLeft: 32,
    "&:before": {
      content: "'Filter'",
      position: "absolute",
      left: 8,
      color: "#475467",
      filter: "opacity(0.7)",
    },
  },
}));

enum TypeFilter {
  DATE_RANGE = "Date Range",
  DATE = "Date",
}

interface TopPanelProps {
  dateTo?: string;
  dateFrom?: string;

  setDateTo: (value?: string) => void;
  setDateFrom: (value?: string) => void;
}

export default function TopPanel(props: TopPanelProps) {
  const { t: translate } = useTranslation();
  const { dateFrom, setDateFrom, setDateTo } = props;

  const [typeFilter, setTypeFilter] = useState<string>(TypeFilter.DATE_RANGE);

  return (
    <Wrapper>
      <SelectStyled
        value={typeFilter}
        onChange={(event: SelectChangeEvent<any>) => {
          setTypeFilter(event.target.value);
          setDateFrom(undefined);
          setDateTo(undefined);
        }}
      >
        <MenuItem value={TypeFilter.DATE_RANGE}>{translate("dateRange")}</MenuItem>
        <MenuItem value={TypeFilter.DATE}>{translate("date")}</MenuItem>
      </SelectStyled>
      {typeFilter === TypeFilter.DATE_RANGE ? (
        <InputDateRange
          showClearButton={true}
          onChange={(range) => {
            if (range.startDate && range.endDate) {
              setDateFrom(
                format(
                  utcToZonedTime(range.startDate, Intl.DateTimeFormat().resolvedOptions().timeZone),
                  "yyyy-MM-dd"
                )
              );
              setDateTo(
                format(
                  utcToZonedTime(range.endDate, Intl.DateTimeFormat().resolvedOptions().timeZone),
                  "yyyy-MM-dd"
                )
              );
            } else {
              setDateFrom(undefined);
              setDateTo(undefined);
            }
          }}
        />
      ) : (
        <>
          <Input
            type="date"
            value={dateFrom?.toString()}
            onChange={(event) => {
              setDateFrom(
                format(
                  utcToZonedTime(
                    event.target.value,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "yyyy-MM-dd"
                )
              );
              setDateTo(
                format(
                  utcToZonedTime(
                    event.target.value,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ),
                  "yyyy-MM-dd"
                )
              );
            }}
          />
          {dateFrom && (
            <Button
              customColor={Color.SECONDARY}
              onClick={() => {
                setDateFrom(undefined);
                setDateTo(undefined);
              }}
            >
              {translate("clear")}
            </Button>
          )}
        </>
      )}
      <Button
        customColor={Color.SECONDARY_OUTLINED}
        onClick={() => {
          setTypeFilter(TypeFilter.DATE);
          setDateFrom(
            format(
              utcToZonedTime(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone),
              "yyyy-MM-dd"
            )
          );
          setDateTo(
            format(
              utcToZonedTime(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone),
              "yyyy-MM-dd"
            )
          );
        }}
        startIcon={<MdDateRange size={16} />}
      >
        {translate("showTodayTransshipments")}
      </Button>
    </Wrapper>
  );
}
